import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, apiErrorMessage, setAccessToken } from '@/services/api/client'
import type { AuthSession, Business, SessionUser } from '@/types/api'
import { AuthContext } from './auth-context'
import { queryClient } from './queryClient'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [business, setBusiness] = useState<Business | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const applySession = useCallback((session: AuthSession) => {
    setAccessToken(session.accessToken)
    setUser(session.user)
    setBusiness(session.business)
    setError(null)
  }, [])

  const refreshProfile = useCallback(async () => {
    const session = await api.post<AuthSession>('/v1/auth/browser/refresh', undefined, { skipAuthRefresh: true })
    applySession(session)
  }, [applySession])

  useEffect(() => {
    refreshProfile().catch(() => {
      setAccessToken(null)
      setUser(null)
      setBusiness(null)
    }).finally(() => setLoading(false))
  }, [refreshProfile])

  const login = useCallback(async (email: string, password: string) => {
    setError(null)
    try {
      const session = await api.post<AuthSession>('/v1/auth/browser/login', { email, password }, { skipAuthRefresh: true })
      applySession(session)
    } catch (cause) {
      const message = apiErrorMessage(cause)
      setError(message)
      throw cause
    }
  }, [applySession])

  const logout = useCallback(async () => {
    try {
      await api.post('/v1/auth/browser/logout', undefined, { skipAuthRefresh: true })
    } finally {
      queryClient.clear()
      setAccessToken(null)
      setUser(null)
      setBusiness(null)
      setError(null)
    }
  }, [])

  const switchEnvironment = useCallback(async (_environment: 'SANDBOX' | 'PRODUCTION') => {
    throw new Error(
      'In-app environment switching is disabled. Use the staging or production deploy URL instead.',
    )
  }, [])

  const hasPermission = useCallback((permission: string) => {
    const permissions = user?.permissions ?? []
    return permissions.includes('*') || permissions.includes(permission)
  }, [user])

  const value = useMemo(() => ({ user, business, loading, error, login, logout, refreshProfile, switchEnvironment, hasPermission }), [user, business, loading, error, login, logout, refreshProfile, switchEnvironment, hasPermission])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
